import { Reducer } from 'redux';

import { BasketActionTypes, BasketActions, BasketState, LoadingItemTypes } from './basket.types';
import { DonatedTypes } from 'models/Basket';
import { deliveryHelper } from 'helpers';

const initialState: BasketState = {
    isBasketLoading: false,
    isBasketPageInitializing: false,
    basket: null,
    basketLoadingItems: [],
    basketLoadingUpsells: [],
    donation: null,
    isDonationLoading: false,
    waitsForChoosingDeliveryType: false,
    isCalculatingDeliveryCost: false,
    deliveryCostError: null,
    isUpsellsLoading: false,
    availableUpsells: [],
    loadedCareKits: [],
    shouldShowReservationWarning: false,
    shouldShowSessionHasExpiredWarning: false,
    isReservationValidationFinished: false,
    shouldShowInvalidItemsRemovingWarning: false,
    isValidatingCart: false,
    validationResult: { isValid: false, sofaShields: [], invalidItems: [] },
    isAllInvalidItemsRemovingProcessing: false,
    isAddingToCartFromUrlProcessing: false
};

export const basketReducer: Reducer<BasketState, BasketActions> = (state = initialState, action) => {
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (action.type) {
        case BasketActionTypes.LOAD_BASKET_START: {
            return { ...state, isBasketLoading: true };
        }

        case BasketActionTypes.LOAD_BASKET_SUCCESS: {
            return {
                ...state,
                isBasketLoading: false,
                basket: action.basket,
                waitsForChoosingDeliveryType: deliveryHelper.shouldWaitForChoosingDeliveryType(action.basket)
            };
        }

        case BasketActionTypes.LOAD_BASKET_ERROR: {
            return { ...state, isBasketLoading: false };
        }

        case BasketActionTypes.INIT_BASKET_PAGE_START: {
            return { ...state, isBasketPageInitializing: true };
        }

        case BasketActionTypes.INIT_BASKET_PAGE_SUCCESS: {
            return {
                ...state,
                isBasketPageInitializing: false
            };
        }

        case BasketActionTypes.INIT_BASKET_PAGE_ERROR: {
            return { ...state, isBasketPageInitializing: false };
        }

        case BasketActionTypes.CLEAR_BASKET: {
            return {
                ...state,
                basket: null
            };
        }

        case BasketActionTypes.LOAD_DONATION_SUCCESS: {
            return {
                ...state,
                donation: action.donation
            };
        }

        case BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_START: {
            return {
                ...state,
                basketLoadingItems: [...state.basketLoadingItems, action.loadingItem]
            };
        }

        case BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_SUCCESS:
        case BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_ERROR: {
            return {
                ...state,
                basketLoadingItems:
                    state.basketLoadingItems.filter(item => (item !== action.loadingItem))
            };
        }

        case BasketActionTypes.REMOVE_BASKET_ITEM_START: {
            return {
                ...state,
                basketLoadingItems: [...state.basketLoadingItems, action.loadingItem],
                isDonationLoading: action.loadingItem.id in DonatedTypes
            };
        }

        case BasketActionTypes.REMOVE_BASKET_ITEM_SUCCESS:
        case BasketActionTypes.REMOVE_BASKET_ITEM_ERROR: {
            return {
                ...state,
                basketLoadingItems: state.basketLoadingItems.filter(item => (item !== action.loadingItem)),
                isDonationLoading: false
            };
        }

        case BasketActionTypes.REMOVE_SOFAGLIDE_START:
        case BasketActionTypes.REMOVE_SOFASHIELD_START: {
            return {
                ...state,
                basketLoadingItems: [...state.basketLoadingItems, action.loadingItem]
            };
        }

        case BasketActionTypes.REMOVE_SOFAGLIDE_SUCCESS:
        case BasketActionTypes.REMOVE_SOFAGLIDE_ERROR:
        case BasketActionTypes.REMOVE_SOFASHIELD_SUCCESS:
        case BasketActionTypes.REMOVE_SOFASHIELD_ERROR: {
            return {
                ...state,
                basketLoadingItems:
                    state.basketLoadingItems.filter(item => (item !== action.loadingItem))
            };
        }

        case BasketActionTypes.UPDATE_SOFAGLIDE_START:
        case BasketActionTypes.UPDATE_SOFASHIELD_START: {
            return {
                ...state,
                basketLoadingItems: [...state.basketLoadingItems, action.loadingItem]
            };
        }

        case BasketActionTypes.UPDATE_SOFAGLIDE_SUCCESS:
        case BasketActionTypes.UPDATE_SOFAGLIDE_ERROR:
        case BasketActionTypes.UPDATE_SOFASHIELD_SUCCESS:
        case BasketActionTypes.UPDATE_SOFASHIELD_ERROR: {
            return {
                ...state,
                basketLoadingItems:
                    state.basketLoadingItems.filter(item => (item !== action.loadingItem))
            };
        }

        case BasketActionTypes.ADD_DONATION_START: {
            return {
                ...state,
                isDonationLoading: true
            };
        }

        case BasketActionTypes.ADD_DONATION_SUCCESS:
        case BasketActionTypes.ADD_DONATION_ERROR: {
            return {
                ...state,
                isDonationLoading: false
            };
        }

        case BasketActionTypes.ADD_UPSELL_START: {
            return {
                ...state,
                basketLoadingUpsells: [...state.basketLoadingUpsells, action.upsellItem.type]
            };
        }

        case BasketActionTypes.ADD_UPSELL_SUCCESS:
        case BasketActionTypes.ADD_UPSELL_ERROR: {
            return {
                ...state,
                basketLoadingUpsells: state.basketLoadingUpsells.filter(item => item !== action.upsellType)
            };
        }

        case BasketActionTypes.CALCULATE_DELIVERY_COST_START: {
            return {
                ...state,
                isCalculatingDeliveryCost: true
            };
        }

        case BasketActionTypes.CALCULATE_DELIVERY_COST_SUCCESS: {
            return {
                ...state,
                isCalculatingDeliveryCost: false,
                deliveryCostError: null
            };
        }

        case BasketActionTypes.CALCULATE_DELIVERY_COST_ERROR: {
            return {
                ...state,
                isCalculatingDeliveryCost: false,
                deliveryCostError: action.error
            };
        }

        case BasketActionTypes.REMOVE_ERROR_OF_CALCULATE_DELIVERY_COST: {
            return {
                ...state,
                deliveryCostError: null
            };
        }

        case BasketActionTypes.SET_DELIVERY_TYPE_START: {
            return {
                ...state,
                isCalculatingDeliveryCost: true
            };
        }

        case BasketActionTypes.SET_DELIVERY_TYPE_SUCCESS: {
            return {
                ...state,
                isCalculatingDeliveryCost: false,
                deliveryCostError: null
            };
        }

        case BasketActionTypes.SET_DELIVERY_TYPE_ERROR: {
            return {
                ...state,
                isCalculatingDeliveryCost: false,
                deliveryCostError: action.error
            };
        }

        case BasketActionTypes.REFRESH_AVAILABLE_UPSELLS_START: {
            return {
                ...state,
                isUpsellsLoading: true
            };
        }

        case BasketActionTypes.REFRESH_AVAILABLE_UPSELLS_SUCCESS: {
            return {
                ...state,
                isUpsellsLoading: false,
                availableUpsells: action.availableUpsells
            };
        }

        case BasketActionTypes.REFRESH_AVAILABLE_UPSELLS_ERROR: {
            return {
                ...state,
                isUpsellsLoading: false,
                availableUpsells: []
            };
        }

        case BasketActionTypes.SAVE_LOADED_CARE_KITS: {
            return {
                ...state,
                loadedCareKits: action.loadedCareKits
            };
        }

        case BasketActionTypes.REMOVE_BASKET_SUCCESS: {
            return {
                ...state,
                basket: null
            };
        }

        case BasketActionTypes.SET_SHOW_SESSION_HAS_EXPIRED_WARNING: {
            return {
                ...state,
                shouldShowSessionHasExpiredWarning: action.shouldShowSessionHasExpiredWarning
            };
        }

        case BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_START: {
            return {
                ...state,
                isReservationValidationFinished: false,
                shouldShowReservationWarning: false
            };
        }

        case BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_SUCCESS:
        case BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_ERROR: {
            return {
                ...state,
                isReservationValidationFinished: true
            };
        }

        case BasketActionTypes.SET_SHOW_RESERVATION_WARNING: {
            return {
                ...state,
                shouldShowReservationWarning: action.shouldShowReservationWarning
            };
        }

        case BasketActionTypes.SET_SHOW_INVALID_ITEMS_REMOVING_WARNING: {
            return {
                ...state,
                shouldShowInvalidItemsRemovingWarning: action.shouldShowInvalidItemsRemovingWarning
            };
        }

        case BasketActionTypes.VALIDATE_CART_START: {
            return {
                ...state,
                isValidatingCart: true,
                validationResult: { isValid: false, sofaShields: [], invalidItems: [] }
            };
        }

        case BasketActionTypes.VALIDATE_CART_SUCCESS: {
            return {
                ...state,
                isValidatingCart: false,
                validationResult: action.validationResult
            };
        }

        case BasketActionTypes.VALIDATE_CART_ERROR: {
            return {
                ...state,
                validationResult: { isValid: false, sofaShields: [], invalidItems: [] },
                isValidatingCart: false,
            };
        }

        case BasketActionTypes.REMOVE_INVALID_ITEMS_START: {
            return {
                ...state,
                isAllInvalidItemsRemovingProcessing: true,
            };
        }

        case BasketActionTypes.REMOVE_INVALID_ITEMS_SUCCESS:
        case BasketActionTypes.REMOVE_INVALID_ITEMS_ERROR: {
            return {
                ...state,
                isAllInvalidItemsRemovingProcessing: false,
                validationResult: { isValid: true, sofaShields: [], invalidItems: [] }
            };
        }

        case BasketActionTypes.REMOVE_ALL_INVALID_ITEMS_START: {
            return {
                ...state,
                basketLoadingItems:
                    [...state.basketLoadingItems].concat(action.loadingItems)
            };
        }

        case BasketActionTypes.REMOVE_ALL_INVALID_ITEMS_SUCCESS:
        case BasketActionTypes.REMOVE_ALL_INVALID_ITEMS_ERROR: {
            return {
                ...state,
                basketLoadingItems: state.basketLoadingItems.filter(
                    item => item.loadingType !== LoadingItemTypes.RemoveItem
                )
            };
        }

        case BasketActionTypes.REMOVE_ALL_SOFASHIELDS_START: {
            return {
                ...state,
                basketLoadingItems:
                    [...state.basketLoadingItems].concat(action.loadingItems)
            };
        }

        case BasketActionTypes.REMOVE_ALL_SOFASHIELDS_SUCCESS:
        case BasketActionTypes.REMOVE_ALL_SOFASHIELDS_ERROR: {
            return {
                ...state,
                basketLoadingItems: state.basketLoadingItems.filter(
                    item => item.loadingType !== LoadingItemTypes.RemoveAllSofashields
                )
            };
        }

        case BasketActionTypes.ADD_TO_CART_FROM_URL_START: {
            return {
                ...state,
                isAddingToCartFromUrlProcessing: true
            };
        }

        case BasketActionTypes.ADD_TO_CART_FROM_URL_SUCCESS: {
            return {
                ...state,
                isAddingToCartFromUrlProcessing: false,
                basket: null
            };
        }

        case BasketActionTypes.ADD_TO_CART_FROM_URL_ERROR: {
            return {
                ...state,
                isAddingToCartFromUrlProcessing: false
            };
        }

        default: {
            return state;
        }
    }
};